import {
  Filter,
  Search,
} from "lucide-react";
import {
  TableState,
} from "../../shared/components/TableState";
import type {
  useSuppliersWorkspace,
} from "./useSuppliersWorkspace";

export function SupplierPortfolio({ model }: { model: ReturnType<typeof useSuppliersWorkspace>; }) {
  const {
    rows,
    query,
    setQuery,
    riskFilter,
    setRiskFilter,
    loading,
    setSelectedSupplierId,
    supplierModalOpenerRef,
    setSupplierTab,
    setSupplierAction,
    setNextStatus,
    visibleRows,
    suppliersWithoutPrimaryContact,
    suppliersNeedingRiskFollowUp,
    supplierStatusLabels,
    contactRoleLabels,
  } = model;
  return (<>
    <section className="module-card supplier-portfolio">
      <div
        className="supplier-portfolio-summary"
        aria-label="Tedarikçi portföy özeti"
      >
        <div>
          <span>Toplam tedarikçi</span>
          <strong>{rows.length}</strong>
          <small>Kayıtlı iş ortağı</small>
        </div>
        <div
          className={suppliersWithoutPrimaryContact ? "needs-attention" : ""}
        >
          <span>İletişim eksik</span>
          <strong>{suppliersWithoutPrimaryContact}</strong>
          <small>Birincil yetkili atanmalı</small>
        </div>
        <div
          className={suppliersNeedingRiskFollowUp ? "needs-attention" : ""}
        >
          <span>Risk takibi</span>
          <strong>{suppliersNeedingRiskFollowUp}</strong>
          <small>Düşük risk dışında</small>
        </div>
      </div>
      <div className="module-toolbar">
        <div className="module-search">
          <Search size={16} />
          <input
            placeholder="Tedarikçi veya kategori ara"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <label className="toolbar-select">
          <Filter size={15} />
          <select
            aria-label="Risk filtresi"
            value={riskFilter}
            onChange={(event) => setRiskFilter(event.target.value)}
          >
            <option>Tümü</option>
            <option>Düşük</option>
            <option>Orta</option>
            <option>İzlenmeli</option>
            <option>Değerlendirilmedi</option>
          </select>
        </label>
      </div>
      {loading ? (
        <TableState
          title="Tedarikçiler yükleniyor"
          description="Tedarikçi ana verisi sunucudan alınıyor."
        />
      ) : visibleRows.length ? (
        <div className="data-table-wrap">
          <table className="data-table supplier-table">
            <thead>
              <tr>
                <th>Tedarikçi</th>
                <th>Satın alma kapsamı</th>
                <th>Performans</th>
                <th>Ana iletişim</th>
                <th>Risk</th>
                <th>
                  <span className="sr-only">İşlem</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((supplier) => (
                <tr className="supplier-portfolio-row" key={supplier.name}>
                  <td data-label="Tedarikçi">
                    <div className="supplier-name">
                      <span>
                        {supplier.name.slice(0, 2).toLocaleUpperCase("tr")}
                      </span>
                      <span>
                        <span className="supplier-title-line">
                          <strong>{supplier.name}</strong>
                          {(supplier.status ?? "ACTIVE") !== "ACTIVE" ? (
                            <span className="status-pill">
                              {
                                supplierStatusLabels[
                                supplier.status ?? "ACTIVE"
                                ]
                              }
                            </span>
                          ) : null}
                        </span>
                        {supplier.legalName ? (
                          <small>{supplier.legalName}</small>
                        ) : null}
                      </span>
                    </div>
                  </td>
                  <td data-label="Satın alma kapsamı">
                    <strong>
                      {supplier.capabilities
                        ?.map(
                          (item) =>
                            item.productGroupName || item.productGroupCode,
                        )
                        .filter(Boolean)
                        .join(", ") || supplier.category}
                    </strong>
                    {supplier.categories && supplier.categories.length > 1 ? (
                      <small>{supplier.categories.join(" · ")}</small>
                    ) : supplier.capabilities &&
                      supplier.capabilities.length > 1 ? (
                      <small>{supplier.capabilities.length} ürün grubu</small>
                    ) : null}
                  </td>
                  <td data-label="Performans">
                    <strong>
                      {supplier.delivery || "Teslimat verisi yok"}
                    </strong>
                    <small>
                      {[
                        supplier.score,
                        supplier.orders ? `${supplier.orders} sipariş` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Henüz ölçülmedi"}
                    </small>
                  </td>
                  <td data-label="Ana iletişim">
                    {supplier.contacts?.find(
                      (item) => item.priority === "PRIMARY",
                    ) ? (
                      <>
                        <strong>
                          {
                            supplier.contacts.find(
                              (item) => item.priority === "PRIMARY",
                            )?.fullName
                          }
                        </strong>
                        <small>
                          {
                            contactRoleLabels[
                            supplier.contacts.find(
                              (item) => item.priority === "PRIMARY",
                            )?.role ?? "RFQ"
                            ]
                          }
                        </small>
                      </>
                    ) : (
                      <span className="supplier-missing-contact">
                        İletişim kişisi eklenmeli
                      </span>
                    )}
                  </td>
                  <td data-label="Risk">
                    <span
                      className={`risk ${supplier.risk.toLocaleLowerCase("tr").startsWith("düşük") ? "risk-düşük" : supplier.risk.toLocaleLowerCase("tr").startsWith("orta") ? "risk-orta" : "risk-izlenmeli"}`}
                    >
                      {supplier.risk}
                    </span>
                  </td>
                  <td className="supplier-row-action">
                    <button
                      className="secondary-button"
                      onClick={() => {
                        supplierModalOpenerRef.current =
                          document.activeElement as HTMLElement | null;
                        setSelectedSupplierId(supplier.id ?? null);
                        setSupplierTab("overview");
                        setSupplierAction(null);
                        setNextStatus(
                          supplier.status === "DRAFT"
                            ? "UNDER_REVIEW"
                            : "ACTIVE",
                        );
                      }}
                    >
                      Aç
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <TableState
          title="Tedarikçi bulunamadı"
          description="Arama ifadesini değiştirerek tekrar deneyin."
        />
      )}
    </section>
  </>);
}
